import { useState, useEffect } from 'react';
import { Box, Typography, TextField, Switch } from '@okta/odyssey-react-mui';
import LabGuide from '../components/LabGuide';
import { Container, Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useFlags, useFlagsmithLoading } from 'flagsmith/react';
import { useNavigate } from 'react-router-dom';
import { PrismLight as SyntaxHighlighter } from 'react-syntax-highlighter';
import markdown from 'react-syntax-highlighter/dist/cjs/languages/prism/markdown';
import { oneDark } from 'react-syntax-highlighter/dist/cjs/styles/prism';

SyntaxHighlighter.registerLanguage('markdown', markdown);

const AuthorTools = () => {
  const flags = useFlags(['authortools']);
  const flagsmithState = useFlagsmithLoading();
  const navigate = useNavigate();
  const [markdownText, setMarkdownText] = useState('# Sample Markdown\n\nEnter your markdown content here...');
  const [previewContent, setPreviewContent] = useState('');
  const [isPreviewMode, setIsPreviewMode] = useState(false);

  useEffect(() => {
    if (!flagsmithState.isLoading && !flagsmithState.isFetching) {
      if (!flags.authortools?.enabled) {
        navigate('/');
      }
    }
  }, [flags, navigate, flagsmithState]);

  const handleTextChange = (event) => {
    setMarkdownText(event.target.value);
  };

  const handleModeToggle = (event) => {
    const newPreviewMode = event.checked;
    
    if (newPreviewMode) {
      // Switching to preview mode - generate preview
      const blob = new Blob([markdownText], { type: 'text/markdown' });
      const url = URL.createObjectURL(blob);
      
      // Clean up old preview URL if it exists
      if (previewContent) {
        URL.revokeObjectURL(previewContent);
      }
      
      setPreviewContent(url);
    }
    
    setIsPreviewMode(newPreviewMode);
  };

  return (
    <Container sx={{ marginTop: '2rem', marginBottom: '2rem' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <Typography variant="h1">
          Author Tools
            <Typography variant='subordinate'>Thank you for helping to make lab content better.</Typography>
        </Typography>
        <Box sx={{ display: 'flex', gap: '1.5rem', alignItems: 'center' }}>
          <Switch 
            label="Preview Mode" 
            isChecked={isPreviewMode}
            onChange={handleModeToggle}
          />
        </Box>
      </Box>
      
      {isPreviewMode ? (
        // Preview Mode
        <Box
          sx={{
            padding: '1.5rem',
            border: '1px solid var(--odyssey-color-border-default)',
            borderRadius: '8px',
            backgroundColor: 'var(--odyssey-color-background-surface)',
            minHeight: '600px',
          }}
        >
          {previewContent ? (
            <LabGuide contentLink={previewContent} />
          ) : (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '400px',
              }}
            >
              <Typography variant="support">
                No content to preview
              </Typography>
            </Box>
          )}
        </Box>
      ) : (
        // Edit Mode
        <>
          <Box sx={{ marginBottom: '2rem' }}>
            <Box>
              <TextField
                label="Markdown Content"
                value={markdownText}
                onChange={handleTextChange}
                isMultiline
                rows={25}
                hint="Enter markdown text to preview"
              />
            </Box>
          </Box>

          {/* labs.demo.okta Style Guide - Only shown in edit mode */}
          <Accordion sx={{ marginBottom: '1rem' }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="style-guide-content"
              id="style-guide-header"
            >
              <Typography variant="h5">
                labs.demo.okta Style Guide
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box
                sx={{
                  padding: '1rem',
                  backgroundColor: 'var(--odyssey-color-background-surface)',
                }}
              >
                <LabGuide contentLink="https://cdn.demo.okta.com/labs/LabContentStyleGuide.md" />
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Syntax Guide - Only shown in edit mode */}
          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="syntax-guide-content"
              id="syntax-guide-header"
            >
              <Typography variant="h5">
                Markdown Syntax Guide
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <Box>
              <Typography variant="legend" sx={{ marginBottom: '0.5rem' }}>Headings</Typography>
              <SyntaxHighlighter language="markdown" style={oneDark} customStyle={{ margin: 0, fontSize: '0.875rem' }}>
{`# Heading 1
## Heading 2
### Heading 3
#### Heading 4`}
              </SyntaxHighlighter>
            </Box>
            
            <Box>
              <Typography variant="legend" sx={{ marginBottom: '0.5rem' }}>Text Formatting</Typography>
              <SyntaxHighlighter language="markdown" style={oneDark} customStyle={{ margin: 0, fontSize: '0.875rem' }}>
{`**bold text**
*italic text*
\`inline code\``}
              </SyntaxHighlighter>
            </Box>
            
            <Box>
              <Typography variant="legend" sx={{ marginBottom: '0.5rem' }}>Links & Images</Typography>
              <SyntaxHighlighter language="markdown" style={oneDark} customStyle={{ margin: 0, fontSize: '0.875rem' }}>
{`[Link text](url)
![Alt text](image-url)`}
              </SyntaxHighlighter>
            </Box>
            
            <Box>
              <Typography variant="legend" sx={{ marginBottom: '0.5rem' }}>Lists</Typography>
              <SyntaxHighlighter language="markdown" style={oneDark} customStyle={{ margin: 0, fontSize: '0.875rem' }}>
{`- Unordered list
1. Ordered list`}
              </SyntaxHighlighter>
            </Box>
            
            <Box>
              <Typography variant="legend" sx={{ marginBottom: '0.5rem' }}>Code Blocks</Typography>
              <SyntaxHighlighter language="markdown" style={oneDark} customStyle={{ margin: 0, fontSize: '0.875rem' }}>
{`\`\`\`language
code block content
\`\`\``}
              </SyntaxHighlighter>
            </Box>
            
            <Box>
              <Typography variant="legend" sx={{ marginBottom: '0.5rem' }}>Tables</Typography>
              <SyntaxHighlighter language="markdown" style={oneDark} customStyle={{ margin: 0, fontSize: '0.875rem' }}>
{`| Header |
|--------|
| Cell   |`}
              </SyntaxHighlighter>
            </Box>
            
            <Box>
              <Typography variant="legend" sx={{ marginBottom: '0.5rem' }}>Alerts</Typography>
              <SyntaxHighlighter language="markdown" style={oneDark} customStyle={{ margin: 0, fontSize: '0.875rem' }}>
{`> [!NOTE]
> Information users should know

> [!TIP]
> Helpful advice

> [!IMPORTANT]
> Crucial information

> [!WARNING]
> Urgent attention needed

> [!CAUTION]
> Potential risks`}
              </SyntaxHighlighter>
            </Box>
            
          </Box>
            </AccordionDetails>
          </Accordion>
        </>
      )}
    </Container>
  );
};

export default AuthorTools;
